import { asList } from "./impactConstants";

// Shared reads over a project's SEGMENTS — the user-defined slices that
// replaced Area and Group. A segment is { id, title, position, choices: [{ id,
// label, position }] }; an impact carries `segment_values: { "<segment id>":
// <choice id> }`, at most one choice per segment.

// An impact with no choice in a segment is "Any": it applies to EVERY choice
// there, so it passes any filter on that segment and counts toward each choice.

// Filters live in the URL as `seg.<segment id>=<choice id>`, one per segment,
// so a filtered view is linkable and back/forward step through it.
export const SEGMENT_PARAM_PREFIX = "seg.";

const byPosition = (a, b) => (a.position ?? 0) - (b.position ?? 0) || Number(a.id) - Number(b.id);

// The segments in display order, each with its choices in order too. The order
// is the user's — it is what "Manage" saved — so it is never re-sorted by name.
export const asSegments = (data) =>
    asList(data)
        .map((segment) => ({ ...segment, choices: [...(segment.choices || [])].sort(byPosition) }))
        .sort(byPosition);

// The choice id an impact holds in a segment, as a string, or null.
export const valueOf = (impact, segmentId) => {
    const value = impact?.segment_values?.[String(segmentId)];
    return value == null || value === "" ? null : String(value);
};

export const choiceOf = (segment, choiceId) =>
    choiceId == null ? null : segment.choices.find((c) => String(c.id) === String(choiceId)) || null;

// `{ segment id: filter value }` from the URL, keeping only segments that exist.
export const filtersFrom = (searchParams, segments) => {
    const filters = {};
    for (const segment of segments) {
        const value = searchParams.get(`${SEGMENT_PARAM_PREFIX}${segment.id}`);
        if (!value) continue;
        // A choice that has since been deleted (or an old `none` link from when
        // "Not set" was a filter) is no filter, not an empty table.
        if (choiceOf(segment, value)) filters[String(segment.id)] = value;
    }
    return filters;
};

// Whether an impact passes every active filter. Filters AND together — that is
// what makes several segments useful at once: "EU" and "Managers" is the
// intersection, not either. An impact that is Any in a segment passes its filter.
export const matchesFilters = (impact, filters) =>
    Object.entries(filters).every(([segmentId, wanted]) => {
        const value = valueOf(impact, segmentId);
        return value == null || value === String(wanted);
    });

// Impacts PLACED on each choice of one segment — exact usage, Any not included.
// What managing a segment needs: how many impacts a rename or delete touches.
export const countsFor = (impacts, segmentId) => {
    const tally = {};
    for (const impact of impacts) {
        const value = valueOf(impact, segmentId);
        if (value != null) tally[value] = (tally[value] || 0) + 1;
    }
    return tally;
};

// What picking each choice as a filter would show: its own impacts plus every
// impact that is Any in this segment, since those apply to every choice.
export const filterCountsFor = (impacts, segment) => {
    const placed = countsFor(impacts, segment.id);
    const any = impacts.filter((impact) => valueOf(impact, segment.id) == null).length;
    return Object.fromEntries(segment.choices.map((c) => [String(c.id), (placed[String(c.id)] || 0) + any]));
};

// [{ segment, choice }] for every segment an impact has a value in, in segment
// order — the shape the table, the detail panel and the export all print.
export const placementsOf = (impact, segments) =>
    segments
        .map((segment) => ({ segment, choice: choiceOf(segment, valueOf(impact, segment.id)) }))
        .filter((entry) => entry.choice);

// Where an impact sits across ALL segments at once, as one thing to group by:
// "Role: Academic staff · Tenure: 10+ years", or just "Role: Academic staff"
// when it is Any in Tenure. `key` is stable across renames (ids, not labels) and
// "" when the impact is Any everywhere. `rank` orders combinations the way the
// segment panel lists them — segment order first, then each segment's choice
// order, with Any after every choice — so "Any everywhere" sorts last.
export const placementOf = (impact, segments) => {
    const placements = placementsOf(impact, segments);
    const chosen = new Map(placements.map(({ segment, choice }) => [String(segment.id), choice]));
    return {
        key: placements.map(({ segment, choice }) => `${segment.id}:${choice.id}`).join("|"),
        label: placements.map(({ segment, choice }) => `${segment.title}: ${choice.label}`).join(" · "),
        rank: segments.map((segment) => {
            const choice = chosen.get(String(segment.id));
            return choice ? segment.choices.indexOf(choice) : segment.choices.length;
        }),
    };
};

export const byRank = (a, b) => {
    for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
        if ((a[i] ?? 0) !== (b[i] ?? 0)) return (a[i] ?? 0) - (b[i] ?? 0);
    }
    return 0;
};
